import {
  AutoComplete,
  Stack,
  Text,
  useBasisFormContext,
  useWatch,
} from "basis";
import { SearchAddress } from "components/AutoAddress/api/addressApi";
import { BoldLineItem } from "components/AutoAddress/BoldLineItem";
import { useAddressSearch } from "components/AutoAddress/hook/useAddressSearch";
import { itemToString } from "components/AutoAddress/utils";
import { getRegional } from "library/getRegional";
import { stateOptions } from "library/sharedOptions";
import { useRef } from "react";
import { AddressMode, PersonalDetailsState } from "../../reducer/types";
import { CantFind } from "./CantFind";
import { EMPTY_ADDRESS_VALUE } from "./constants";
import { ManualAddress } from "./ManualAddress";
import { Switch } from "./Switch";

export const AddressField = () => {
  const { methods, Field } = useBasisFormContext<PersonalDetailsState>();
  const { setValue, control, getValues } = methods;
  const countryCode = getRegional("code");

  const mode = useWatch<PersonalDetailsState>({
    control,
    name: "applicantDetails.address.mode",
  }) as AddressMode;

  const ref = useRef<HTMLInputElement>(null);
  const { search } = useAddressSearch({ countryCode });

  const switchToManual = () => {
    setValue("applicantDetails.address.mode", "manual");
  };

  const onAddressChange = (autoAddress: SearchAddress) => {
    const address = getValues("applicantDetails.address");
    if (!autoAddress) {
      setValue("applicantDetails.address", {
        ...address,
        suburb: "",
        postCode: "",
        state: "",
      });
      return;
    }
    const state = stateOptions.find(
      ({ value: stateValue }) =>
        stateValue === String(autoAddress.State).toLowerCase(),
    );
    setValue("applicantDetails.address", {
      ...address,
      streetAddress: autoAddress.AddressLine,
      suburb: autoAddress.Locality,
      postCode: autoAddress.Postcode,
      state: state?.value ?? "",
      city: autoAddress.State,
    });
  };

  const switchToAutoSearch = () => {
    setValue("applicantDetails.address.mode", "auto");
    ref.current?.focus();
  };

  if (mode === "auto") {
    return (
      <Stack gap="4">
        <Field
          name="applicantDetails.address.auto"
          label="Address"
          placeholder="Search here!"
          emptyValue={EMPTY_ADDRESS_VALUE}
          // @ts-ignore
          onChange={onAddressChange}
          // @ts-ignore
          getItems={search}
          // @ts-ignore
          itemToString={itemToString}
          // @ts-ignore
          listItem={BoldLineItem}
          renderItemsFooter={() => <CantFind />}
          as={AutoComplete}
          onItemsFooterSelect={switchToManual}
        />
        <Switch onClick={switchToManual}>Enter manually</Switch>
      </Stack>
    );
  }

  return (
    <Stack gap="8">
      <div>
        <Text color="grey.t75">
          <strong>Address</strong>
        </Text>
        <Text>
          Enter manually, or{" "}
          <Switch onClick={switchToAutoSearch}>
            <b>return to address finder</b>
          </Switch>
        </Text>
      </div>
      <ManualAddress countryCode={countryCode} />
    </Stack>
  );
};
